import { motion } from "framer-motion";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";
import { goExternal, BUSINESS_URL } from "@/lib/links";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative z-10 bg-[#F7F5F2] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left — heading + signature */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="overline mb-6">The Vision Behind Book Away</p>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight text-[#2A2724]"
              >
                Never lose revenue to an{" "}
                <span className="italic text-gold">empty chair</span> again.
              </motion.h2>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-10 hidden lg:block"
              >
                <p className="font-serif text-4xl italic text-[#2A2724]">Bella xx</p>
                <p className="mt-2 text-sm uppercase tracking-[0.24em] text-[#9a9a9a]">
                  Founder, Book Away · Sydney
                </p>
              </motion.div>
            </div>
          </div>

          {/* Right — narrative */}
          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-7"
          >
            <motion.p
              variants={fadeUp}
              className="text-lg leading-relaxed text-[#3a3a3a] md:text-xl"
            >
              I'm Bella, and I founded Book Away with a clear goal: to ensure that
              a last-minute cancellation and appointments that are not filled{" "}
              <span className="font-medium text-[#2A2724]">never means lost revenue</span>{" "}
              for your business.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg leading-relaxed text-[#6B6B6B]"
            >
              Every salon owner knows the frustration of a sudden gap in the
              schedule that stays empty. I built Book Away to solve that problem
              instantly. We're building Australia's most dynamic network to help
              you{" "}
              <span className="font-medium text-[#2A2724]">
                fill those last-minute cancellations and appointments
              </span>{" "}
              that would otherwise go to waste.
            </motion.p>

            {/* Standout mission pull-quote */}
            <motion.blockquote
              variants={fadeUp}
              className="relative my-12 border-l-2 border-gold pl-8"
            >
              <p className="font-serif text-3xl font-light leading-tight text-[#2A2724] md:text-4xl lg:text-5xl">
                Our mission is to{" "}
                <span className="italic text-gold">turn your downtime into revenue.</span>
              </p>
            </motion.blockquote>

            <motion.p
              variants={fadeUp}
              className="text-lg leading-relaxed text-[#6B6B6B]"
            >
              We're starting here in Sydney, but our vision is to become the
              national standard for on-demand booking. We're building a future
              where your schedule is always full, and your customers are always
              just a tap away.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg leading-relaxed text-[#6B6B6B]"
            >
              We're growing fast, and we'd love for you to join us on the ground
              floor as a{" "}
              <span className="font-medium text-[#2A2724]">Foundation Member.</span>
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-8 font-serif text-2xl italic text-[#2A2724] md:text-3xl"
            >
              It's time to get Booked In.
            </motion.p>

            {/* Signature — mobile */}
            <motion.div variants={fadeUp} className="mt-8 lg:hidden">
              <p className="font-serif text-3xl italic text-[#2A2724]">Bella xx</p>
              <p className="mt-2 text-sm uppercase tracking-[0.24em] text-[#9a9a9a]">
                Founder, Book Away · Sydney
              </p>
            </motion.div>

            <motion.button
              variants={fadeUp}
              data-testid="about-foundation-cta"
              onClick={() => goExternal(BUSINESS_URL)}
              transition={{ duration: 0.4, ease: EASE }}
              className="mt-10 inline-flex items-center rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627]"
            >
              Become a Foundation Member
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
